import { useEffect, useState } from 'react';
import { apiBase } from '../lib/api';

export type DonationsStatus = 'checking' | 'available' | 'unavailable';

/**
 * Probes the donation backend so the UI never offers a checkout that cannot complete.
 * Donations are gated on the backend being reachable because it — not this SPA — holds the
 * Stripe secret key and creates the Checkout Session.
 *
 * Probed once on mount. Nobody sits on the page waiting for donations to open, so polling
 * would just be traffic; a reload picks up the change.
 */
const PROBE_TIMEOUT_MS = 8_000;

export const useDonationsAvailable = (): DonationsStatus => {
  const [status, setStatus] = useState<DonationsStatus>('checking');

  useEffect(() => {
    let cancelled = false;

    const probe = async (): Promise<void> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
      try {
        const res = await fetch(`${apiBase}/health`, { signal: controller.signal });
        // Any 2xx means the API answered; a body of {"status":"OK"} is the healthy case.
        const ok = res.ok && (await res.json().catch(() => null))?.status === 'OK';
        if (!cancelled) setStatus(ok ? 'available' : 'unavailable');
      } catch {
        if (!cancelled) setStatus('unavailable');
      } finally {
        clearTimeout(timer);
      }
    };

    void probe();
    return () => { cancelled = true; };
  }, []);

  return status;
};
